import React from 'react';
import { AttachMoney, Inventory } from '@mui/icons-material';
import { TimeGranularityToggle } from '../../../components/TimeGranularityToggle';
import type { TimeGranularity } from '../../../utils/timeGranularity';

export interface ChartControlsProps {
  title: string;
  subtitle?: string;
  isQuantity: boolean;
  setIsQuantity: (_value: boolean) => void;
  timeGranularity: TimeGranularity;
  setTimeGranularity: (_value: TimeGranularity) => void;
  viewMonths: number;
  setViewMonths: (_value: number) => void;
  /** Which granularity options to show in the toggle. Defaults to ['Month', 'Terminal']. */
  granularityOptions?: TimeGranularity[];
  /** Hide the value/qty toggle (e.g. for hedges-deriving which is always monetary) */
  hideValueQtyToggle?: boolean;
  /** Hide the subtitle when in quantity mode */
  hideSubtitleInQtyMode?: boolean;
  /** Extra controls to render between title and toggles (e.g. calc button, save button) */
  extraControls?: React.ReactNode;
  /** Extra controls to render inside the toggles row */
  extraToggles?: React.ReactNode;
  /** Disable the granularity toggle (shows current value as non-interactive pill) */
  disableGranularity?: boolean;
}

export const ChartControls: React.FC<ChartControlsProps> = ({
  title,
  subtitle = 'Values are based on latest spot prices and FX rates',
  isQuantity,
  setIsQuantity,
  timeGranularity,
  setTimeGranularity,
  viewMonths,
  setViewMonths,
  granularityOptions = ['Month', 'Terminal'],
  hideValueQtyToggle = false,
  hideSubtitleInQtyMode = true,
  extraControls,
  extraToggles,
  disableGranularity = false,
}) => {
  return (
    <div className="flex items-center justify-between px-3 pt-3 mb-2">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          {title}
          {extraControls}
        </h2>
        {subtitle && (
          <p
            className={`text-[10px] text-gray-500 mt-1 ${
              hideSubtitleInQtyMode && isQuantity ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {extraToggles}

        {/* Value/Quantity Toggle */}
        {!hideValueQtyToggle && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setIsQuantity(false)}
              className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                !isQuantity
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title={isQuantity ? 'Value' : undefined}
            >
              <AttachMoney className="w-4 h-4" />
              {!isQuantity && <span className="text-xs">Value</span>}
            </button>
            <button
              onClick={() => setIsQuantity(true)}
              className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                isQuantity
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title={!isQuantity ? 'Quantity' : undefined}
            >
              <Inventory className="w-4 h-4" />
              {isQuantity && <span className="text-xs">Qty</span>}
            </button>
          </div>
        )}

        {/* Time Granularity Toggle */}
        <TimeGranularityToggle
          value={timeGranularity}
          onChange={setTimeGranularity}
          options={granularityOptions}
          disabled={disableGranularity}
        />

        {/* Compact Year Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setViewMonths(12)}
            className={`px-3 py-1.5 rounded-full transition-colors ${
              viewMonths === 12
                ? 'bg-white text-gray-900 shadow-sm font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="text-xs">{viewMonths === 12 ? '1 Year' : '1'}</span>
          </button>
          <button
            onClick={() => setViewMonths(24)}
            className={`px-3 py-1.5 rounded-full transition-colors ${
              viewMonths === 24
                ? 'bg-white text-gray-900 shadow-sm font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="text-xs">{viewMonths === 24 ? '2 Years' : '2'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
